import * as React from 'react';
import { useEffect } from 'react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import AutoScroll, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import { SubtleOverscroll } from './subtle-overscroll';

import useEmblaCarousel, {
    type UseEmblaCarouselType,
} from 'embla-carousel-react';

export type CarouselApi = UseEmblaCarouselType[1];

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
export type CarouselOptions = UseCarouselParameters[0];

export interface OnChangeSlideEvent {
    currentIndex: number;
    prevIndex: number;
    nextIndex: number;
}

interface CarouselProps extends React.PropsWithChildren {
    ref?: React.RefObject<HTMLDivElement>;
    options?: CarouselOptions;
    orientation?: 'horizontal' | 'vertical';
    setApi?: (api: CarouselApi) => void;
    onChangeSlide?: (event: OnChangeSlideEvent) => void;
    onSettleSlide?: (event: OnChangeSlideEvent) => void;
    autoPlay?: boolean | AutoplayOptionsType;
    /**
     * Accessible name for the carousel's `region` landmark. Required for the
     * landmark to be exposed distinctly when a page has several carousels —
     * name the content ("Similar products"), not the widget.
     */
    ariaLabel?: string;
}

interface CarouselContextProps extends CarouselProps {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    currentSlide: number;
    totalSlides: number;
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

export const useCarousel = () => {
    const context = React.useContext(CarouselContext);

    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />');
    }

    return context;
};

export const Carousel = (props: CarouselProps) => {
    const {
        ref,
        orientation = 'horizontal',
        options,
        setApi,
        onChangeSlide,
        onSettleSlide,
        children,
        autoPlay,
    } = props;

    const shouldAutoPlay = Boolean(autoPlay);
    // The visibility effect below is the single authority over play/stop, so
    // embla's own interaction/hover/focus autonomy is turned off here: autoplay
    // never starts on mount, stops on pointer-down, and never resumes itself —
    // we re-assert the intended state on every relevant signal instead.
    const autoPlayOptions: AutoplayOptionsType = {
        ...(typeof autoPlay === 'object' ? autoPlay : {}),
        playOnInit: false,
        stopOnInteraction: true,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
    };
    // Registration is gated on reduced-motion (resolved client-side in the
    // effect below): leaving the plugin out entirely for those users stops embla
    // from auto-resuming it on drag/hover.
    const [autoplayEnabled, setAutoplayEnabled] = React.useState(false);

    const [carouselRef, api] = useEmblaCarousel(
        {
            ...options,
            axis: orientation === 'horizontal' ? 'x' : 'y',
        },
        [
            WheelGesturesPlugin({
                forceWheelAxis: orientation === 'horizontal' ? 'x' : 'y',
            }),
            // Apple-style subtle edge rubber band (no-op on loop carousels —
            // the engine only constrains bounds when not looping).
            SubtleOverscroll(),
            ...(autoplayEnabled ? [AutoScroll(autoPlayOptions)] : []),
        ],
    );

    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [totalSlides, setTotalSlides] = React.useState(0);

    const onSelect = React.useCallback(
        (api: CarouselApi) => {
            if (!api) {
                return;
            }

            const currentIndex = api.selectedScrollSnap();
            const totalSlides = api.scrollSnapList().length;

            setCanScrollPrev(api.canScrollPrev());
            setCanScrollNext(api.canScrollNext());
            setCurrentSlide(currentIndex);
            setTotalSlides(totalSlides);

            if (onChangeSlide) {
                onChangeSlide({
                    currentIndex,
                    prevIndex: (currentIndex - 1 + totalSlides) % totalSlides,
                    nextIndex: (currentIndex + 1) % totalSlides,
                });
            }
        },
        [onChangeSlide],
    );

    const onSettle = React.useCallback(
        (api: CarouselApi) => {
            if (!api) {
                return;
            }

            const currentIndex = api.selectedScrollSnap();
            const totalSlides = api.scrollSnapList().length;

            onSettleSlide?.({
                currentIndex,
                prevIndex: (currentIndex - 1 + totalSlides) % totalSlides,
                nextIndex: (currentIndex + 1) % totalSlides,
            });
        },
        [onSettleSlide],
    );

    const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api]);

    const scrollNext = React.useCallback(() => api?.scrollNext(), [api]);

    useEffect(() => {
        if (!api || !setApi) {
            return;
        }

        setApi(api);
        setTotalSlides(api.scrollSnapList().length);
    }, [api, setApi]);

    useEffect(() => {
        if (!api) {
            return;
        }

        onSelect(api);
        api.on('reInit', onSelect);
        api.on('select', onSelect);
        api.on('settle', onSettle);

        return () => {
            api?.off('select', onSelect);
            api?.off('settle', onSettle);
        };
    }, [api, onSelect, onSettle]);

    // Resolve reduced-motion preference client-side (SSR-safe) and react to
    // changes; autoplay stays disabled while the user prefers reduced motion.
    useEffect(() => {
        if (!shouldAutoPlay) {
            setAutoplayEnabled(false);
            return;
        }
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setAutoplayEnabled(!query.matches);
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, [shouldAutoPlay]);

    // Single authority over autoplay: it runs only while the carousel is in view
    // and the user isn't engaging with it — hovering or keyboard-focusing any
    // part, the pagination controls included. We re-assert that on every signal
    // that changes the desired state or hands us a fresh plugin instance:
    // intersection, hover, focus, an embla reInit, and the end of a drag.
    useEffect(() => {
        if (!autoplayEnabled || !api) {
            return;
        }

        // Pagination pills render as siblings of the embla viewport inside the
        // carousel region, so hover/focus is tracked on that wider wrapper
        // rather than rootNode() — otherwise using the controls wouldn't pause.
        const root = api.rootNode();
        const scope = root.parentElement ?? root;
        let inView = false;
        let hovered = false;
        let focused = false;

        const sync = () => {
            // Resolve the plugin per-call: a reInit swaps the instance, so a
            // closed-over reference would go stale.
            const autoplay = api.plugins().autoplay;
            if (!autoplay) {
                return;
            }
            // `:hover` covers the carousel scrolling in beneath a stationary
            // cursor, where no mouseenter fires and `hovered` stays false.
            const shouldPlay =
                inView && !hovered && !focused && !scope.matches(':hover');
            if (shouldPlay && !autoplay.isPlaying()) {
                autoplay.play();
            } else if (!shouldPlay && autoplay.isPlaying()) {
                autoplay.stop();
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                inView = Boolean(entries[0]?.isIntersecting);
                sync();
            },
            { threshold: 0.2 },
        );
        observer.observe(scope);

        const onEnter = () => {
            hovered = true;
            sync();
        };
        const onLeave = () => {
            hovered = false;
            sync();
        };
        const onFocusIn = () => {
            focused = true;
            sync();
        };
        const onFocusOut = (event: FocusEvent) => {
            // Stay paused while focus moves between elements inside the carousel;
            // resume only once it leaves the carousel entirely.
            if (!scope.contains(event.relatedTarget as Node | null)) {
                focused = false;
                sync();
            }
        };
        scope.addEventListener('mouseenter', onEnter);
        scope.addEventListener('mouseleave', onLeave);
        scope.addEventListener('focusin', onFocusIn);
        scope.addEventListener('focusout', onFocusOut);

        // reInit hands us a fresh (stopped) plugin; pointerUp ends a drag that
        // embla paused. Re-assert intended state after both.
        api.on('reInit', sync);
        api.on('pointerUp', sync);

        return () => {
            observer.disconnect();
            scope.removeEventListener('mouseenter', onEnter);
            scope.removeEventListener('mouseleave', onLeave);
            scope.removeEventListener('focusin', onFocusIn);
            scope.removeEventListener('focusout', onFocusOut);
            api.off('reInit', sync);
            api.off('pointerUp', sync);
        };
    }, [api, autoplayEnabled]);

    return (
        <CarouselContext.Provider
            value={{
                carouselRef,
                api,
                options,
                orientation:
                    orientation ||
                    (options?.axis === 'y' ? 'vertical' : 'horizontal'),
                scrollPrev,
                scrollNext,
                canScrollPrev,
                canScrollNext,
                currentSlide,
                totalSlides,
            }}
        >
            <div
                ref={ref}
                className="relative h-full"
                role="region"
                aria-roledescription="carousel"
                aria-label={props.ariaLabel}
            >
                {children}
            </div>
        </CarouselContext.Provider>
    );
};
